import { Environments } from 'src/configurations'
import {
  ReceiptRecoveryCase,
  ReceiptRecoveryLifecycleState,
} from 'src/models/transaction.models'

import { ReceiptEvidenceAttachmentPreviewType } from './receiptEvidence'

interface GetReceiptRecoveryCaseForDisplayParams {
  enabled: boolean
  mockDataEnabled: boolean
  receiptRecoveryCase?: ReceiptRecoveryCase
  localPreviewState?: ReceiptRecoveryLifecycleState
}

export const receiptRecoveryPreviewSearchParam = 'receiptRecoveryPreview'
export const receiptEvidenceAttachmentPreviewSearchParam =
  'receiptEvidenceAttachmentPreview'

const localPreviewStateByName: Record<string, ReceiptRecoveryLifecycleState> = {
  'waiting-on-spender': ReceiptRecoveryLifecycleState.WaitingOnSpender,
  'missing-receipt': ReceiptRecoveryLifecycleState.MissingReceipt,
  'needs-review': ReceiptRecoveryLifecycleState.NeedsReview,
  'receipt-reconciled': ReceiptRecoveryLifecycleState.ReceiptReconciled,
}

const localAttachmentPreviewTypeByName: Record<
  string,
  ReceiptEvidenceAttachmentPreviewType
> = {
  image: 'image',
  pdf: 'pdf',
  'in-app-upload': 'in-app-upload',
  'transaction-upload': 'transaction-upload',
  email: 'email',
  'phone-message': 'phone-message',
}

const getLocalPreviewCase = (
  lifecycleState: ReceiptRecoveryLifecycleState,
): ReceiptRecoveryCase => {
  switch (lifecycleState) {
    case ReceiptRecoveryLifecycleState.WaitingOnSpender:
      return {
        lifecycleState,
        lastAgentRunAt: '2026-04-22T12:14:00Z',
      }
    case ReceiptRecoveryLifecycleState.MissingReceipt:
      return {
        lifecycleState,
        lastAgentRunAt: '2026-04-22T12:14:00Z',
      }
    case ReceiptRecoveryLifecycleState.NeedsReview:
      return {
        lifecycleState,
        recoveredFrom: 'GMAIL',
      }
    case ReceiptRecoveryLifecycleState.ReceiptReconciled:
      return {
        lifecycleState,
        reconciledByDisplayName: 'Local preview',
        reconciledAt: '2026-04-23T13:12:00Z',
      }
  }
}

export const getReceiptRecoveryPreviewStateFromSearch = (
  search: string,
): ReceiptRecoveryLifecycleState | undefined =>
  localPreviewStateByName[
    new URLSearchParams(search).get(receiptRecoveryPreviewSearchParam) ?? ''
  ]

export const getReceiptEvidenceAttachmentPreviewTypeFromSearch = (
  search: string,
): ReceiptEvidenceAttachmentPreviewType | undefined =>
  localAttachmentPreviewTypeByName[
    new URLSearchParams(search).get(
      receiptEvidenceAttachmentPreviewSearchParam,
    ) ?? ''
  ]

interface GetLocalReceiptRecoveryPreviewStateParams {
  enabled: boolean
  mockDataEnabled: boolean
  environment: Environments
  search: string
}

export const getLocalReceiptRecoveryPreviewState = ({
  enabled,
  mockDataEnabled,
  environment,
  search,
}: GetLocalReceiptRecoveryPreviewStateParams):
  | ReceiptRecoveryLifecycleState
  | undefined => {
  if (!enabled || !mockDataEnabled || environment !== Environments.Dev) {
    return undefined
  }

  return getReceiptRecoveryPreviewStateFromSearch(search)
}

export const getLocalReceiptEvidenceAttachmentPreviewType = ({
  enabled,
  mockDataEnabled,
  environment,
  search,
}: GetLocalReceiptRecoveryPreviewStateParams):
  | ReceiptEvidenceAttachmentPreviewType
  | undefined => {
  if (!enabled || !mockDataEnabled || environment !== Environments.Dev) {
    return undefined
  }

  return getReceiptEvidenceAttachmentPreviewTypeFromSearch(search)
}

export const getReceiptRecoveryCaseForDisplay = ({
  enabled,
  mockDataEnabled,
  receiptRecoveryCase,
  localPreviewState,
}: GetReceiptRecoveryCaseForDisplayParams): ReceiptRecoveryCase | undefined => {
  if (!enabled) return undefined
  if (receiptRecoveryCase) return receiptRecoveryCase
  if (!mockDataEnabled) return undefined
  if (localPreviewState) return getLocalPreviewCase(localPreviewState)

  // Local-only mock data used until the Receipt Agent API supplies a case.
  return getLocalPreviewCase(ReceiptRecoveryLifecycleState.NeedsReview)
}
